import { useState } from "react"
import { Box, Button, ButtonGroup, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material"
import { useSnackbar } from "notistack"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/if"
import { Else } from "lib/@components/else"
import { useDialog } from "lib/@hooks/useDialog"
import Iconify from "minimals-template/components/Iconify"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { NoResults } from "lib/@components/no-results/no-results"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { confirm } from "lib/@dialogs/confirm"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { CopDocumentsTabActions } from "./cop-slot-definitions"
import { getCopDocuments } from "./controller/get-cop-documents.controller"
import { saveCopDocument } from "./controller/save-cop-document.controller"
import { uploadCopDocument } from "./controller/upload-cop-document.controller"
import { searchRegionalGuidance } from "./controller/search-regional-guidance.controller"
import { CopDocumentRow } from "./cop-document-row"
import { CopDocumentPendingRow } from "./cop-document-pending-row"
import { CopDocumentAddUrlDialog } from "./cop-document-add-url-dialog"
import { CopDocumentUploadDialog } from "./cop-document-upload-dialog"
import { useActiveDocumentIds } from "./use-active-document-ids"

export function CopDocumentList({ codeOfPracticeId }) {
    const documents = getCopDocuments.useResults({ codeOfPracticeId }, withInitialValue(IS_LOADING))
    const [adding, setAdding] = useState(null)
    const activeDocumentIds = useActiveDocumentIds()
    const { country } = useBoundContext()
    const { enqueueSnackbar } = useSnackbar()
    const openAddUrl = useDialog(<CopDocumentAddUrlDialog />, { maxWidth: "sm", fullWidth: true })
    const openUpload = useDialog(<CopDocumentUploadDialog />, { maxWidth: "sm", fullWidth: true })

    if (documents === IS_LOADING) return <LoadingScreen />

    const showTable = documents.length > 0 || adding

    return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <CopDocumentsTabActions.Plug>
                <ButtonGroup size="small" disableElevation variant="outlined">
                    <Button startIcon={<Iconify icon="mdi:link-plus" />} onClick={handleAddUrl}>
                        Add URL
                    </Button>
                    <Button startIcon={<Iconify icon="mdi:file-upload" />} onClick={handleUpload}>
                        Upload PDF
                    </Button>
                    <Button
                        startIcon={<Iconify icon="mdi:web-search" />}
                        onClick={handleSearchGuidance}
                        disabled={!country}
                    >
                        Search for Regional Guidance
                    </Button>
                </ButtonGroup>
            </CopDocumentsTabActions.Plug>
            <If value={showTable}>
                <Box sx={{ flex: 1, overflowY: "auto" }}>
                    <Table stickyHeader size="small" sx={{ "& .MuiTableCell-root": { px: 1, py: 0.5 } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 24, px: 0.5 }} />
                                <TableCell>Name</TableCell>
                                <TableCell sx={{ width: 110 }}>Authority</TableCell>
                                <TableCell>Source</TableCell>
                                <TableCell align="center" sx={{ width: 80 }}>
                                    Status
                                </TableCell>
                                <TableCell align="right">Data Captured</TableCell>
                                <TableCell align="right" sx={{ width: 80 }}>
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <Bound activeDocumentIds={activeDocumentIds}>
                                <Repeat list={documents} item={<CopDocumentRow />} keyFn={keyById} />
                            </Bound>
                            <If value={adding}>
                                <CopDocumentPendingRow name={adding?.name} type={adding?.type} />
                            </If>
                        </TableBody>
                    </Table>
                </Box>
                <Else>
                    <NoResults message="No documents yet. Add a URL or upload a PDF." />
                </Else>
            </If>
        </Box>
    )

    async function handleSearchGuidance() {
        const ok = await confirm(
            `This will search the web for authoritative maintenance guidance applicable to ${country}. ` +
                "Found sources will be added as documents, scraped, and curated for maintenance relevance.\n\n" +
                "This may take a few minutes.",
            "Search for Regional Guidance"
        )
        if (!ok) return
        await searchRegionalGuidance({ codeOfPracticeId })
        enqueueSnackbar("Search started — check the progress log for updates", { variant: "info" })
    }

    async function handleAddUrl() {
        const data = await openAddUrl()
        if (!data) return
        setAdding({ name: data.name, type: "url" })
        try {
            await saveCopDocument({ codeOfPracticeId, name: data.name, url: data.url })
        } finally {
            setAdding(null)
        }
    }

    async function handleUpload() {
        const data = await openUpload()
        if (!data) return
        setAdding({ name: data.name, type: "file" })
        try {
            await uploadCopDocument({
                codeOfPracticeId,
                name: data.name,
                fileName: data.fileName,
                fileData: data.fileData,
            })
        } finally {
            setAdding(null)
        }
    }
}

function keyById(item) {
    return item._id
}
